import React, { Fragment, JSX, ReactNode, useState } from 'react';
import {
  ApiBookedItem,
  ApiBooking,
  ApiComponentType,
  ApiFlightItem,
  ApiItemType,
  ApiPrice,
  ApiPriceModifierType,
  ApiTraveler
} from '@ibe/api';
import styled from '@emotion/styled';
import { PriceFactory } from '@ibe/services';
import useTranslation from '../../../Hooks/useTranslation';
import Items from '../../../Items/index';
import { Price } from '../../UI/Price/Price';
import {
  BookedItemComponentProps,
  CustomItemRegistry,
  ExtendedApiBookedItem,
  ItemComponentView,
  mergeItemRegisters,
  SummaryOrderType
} from '../../../../Models/ItemRegistry';
import BookedItemComponent from '../BookedItemComponent/BookedItemComponent';
import fallback from '../../../../_Translations/generated/summary.en.t.json';
import Keys from '../../../../_Translations/generated/summary.en.t.json.keys';
import { Button, Col, Collapse, Row } from 'reactstrap';
import BasicModal from '../../../Modules/Customizer/PackageCustomizer/BasicModal';
import PriceUtil from '../../../../MMB/Components/Util/PriceUtil';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface SummaryProperties {
  view?: ItemComponentView;
  booking: ApiBooking | null;
  isEditable?: boolean;
  showTotal?: boolean;
  showDivider?: boolean;
  onDelete?: (idItem: string) => Promise<void>;
  onEdit?: (idItem: string) => Promise<void>;
  bookedItemsOpen?: boolean;
  showInfo?: boolean;
  showTraveler?: boolean;
  showTravelerSeparate?: boolean;
  travelers?: ApiTraveler[];
  showSsr?: boolean;
  showAdditionalInfo?: boolean;
  withPriceDetailsModal?: boolean;
  showSeats?: boolean;
  bookedItems?: ExtendedApiBookedItem[];
  withIconColumn?: boolean;
  hideSinglePrices?: boolean;
  header?: string | JSX.Element;
  customFlightDateDisplay?: (a: ApiFlightItem) => JSX.Element;
  customPriceModifier?: (a: ApiBookedItem, id?: string) => ApiPrice | undefined;
  showNumberOfPerson?: boolean;
  order?: SummaryOrderType;
  showDiscounts?: boolean;
  showFees?: boolean;
  showSsrPriceSubtotal?: boolean;
  groupSameItems?: boolean;
  bookedItemsContainerClass?: string;
  relatedItems?: Map<ApiBookedItem, ApiBookedItem[]>;
  showPackageRelatedItems?: boolean;
  displayExtraServiceDate?: (item: ApiBookedItem) => boolean;
  displayExtraServiceTime?: (item: ApiBookedItem) => boolean;
  displayRelatedItems?: (item: ApiBookedItem) => boolean;
  showPaxInSummary?: boolean;
  useCustomItemOrder?: boolean;
  renderExternalContent?: JSX.Element;
  showPricePlusMinus?: (item: ApiBookedItem) => boolean;
  customItemRegistry?: CustomItemRegistry;
  showOnlyPriceOfNewItems?: boolean;
  isCollapsible?: boolean;
  onCollapseToggle?: (a: number) => void;
  activeCollapsibleIndexes?: number[];
  customLabelsMapper?: BookedItemComponentProps['customLabelsMapper'];
  isInEditMode?: boolean;
  customPerTravelerSummaryItem?: (
    traveler: ApiTraveler,
    bookedItemList: BookedItemType[],
    travelerIdx: number
  ) => JSX.Element;
}

const TaxSpan = styled.span`
  font-size: 0.8rem;
`;

type BookedItemType = {
  item: ExtendedApiBookedItem;
  amount: number;
};

const isOptionalFlight = (item: ApiBookedItem): boolean => {
  return item.itemType === ApiItemType.FLIGHT && item.componentType === ApiComponentType.OPTIONAL;
};

const isRequiredFlight = (item: ApiBookedItem): boolean => {
  return item.itemType === ApiItemType.FLIGHT && item.componentType === ApiComponentType.REQUIRED;
};

export const getBookedItemsList = (
  bookedItems?: ExtendedApiBookedItem[],
  bookingBookedItems?: ApiBookedItem[],
  groupSameItems?: boolean,
  showPackageRelatedItems?: boolean,
  hasRelatedItems?: (item: ApiBookedItem) => boolean,
  travelerId?: string
): BookedItemType[] => {
  const bookedItemsSource = showPackageRelatedItems
    ? bookedItems || bookingBookedItems || []
    : bookedItems ||
      bookingBookedItems?.filter(item => item.relatedBookedItemId === undefined) ||
      [];
  let currentBookedItems = bookedItemsSource.map(item => {
    return {
      item,
      amount: 1
    };
  });
  if (groupSameItems) {
    let newList: BookedItemType[] = [];
    const hasRelatedItemsPredicate = (groupedItem: ApiBookedItem, item: ApiBookedItem): boolean => {
      return (
        groupedItem.name === item.name &&
        groupedItem.relatedBookedItemId === item.relatedBookedItemId &&
        !!hasRelatedItems &&
        hasRelatedItems(item)
      );
    };

    currentBookedItems.forEach(item => {
      const foundIndex = newList.findIndex(
        listItem =>
          (listItem.item.name === item.item.name &&
            listItem.item.startDate === item.item.startDate &&
            listItem.item.relatedBookedItemId === item.item.relatedBookedItemId &&
            listItem.item.endDate === item.item.endDate) ||
          hasRelatedItemsPredicate(listItem.item, item.item)
      );
      if (foundIndex > -1) {
        newList.splice(foundIndex, 1, {
          item: {
            ...newList[foundIndex].item,
            price: {
              ...newList[foundIndex].item.price,
              basePrice: newList[foundIndex].item.price.basePrice + item.item.price.basePrice,
              finalPrice: newList[foundIndex].item.price.finalPrice + item.item.price.finalPrice
            }
          },
          amount: hasRelatedItemsPredicate(newList[foundIndex].item, item.item)
            ? newList[foundIndex].amount
            : newList[foundIndex].amount + 1
        });
      } else {
        newList = [...newList, { item: item.item, amount: 1 }];
      }
    });
    currentBookedItems = [...newList];
  }

  if (showPackageRelatedItems) {
    currentBookedItems = [
      ...currentBookedItems.filter(
        item =>
          item.item.relatedBookedItemId !== undefined || item.item.itemType === ApiItemType.PACKAGE
      ),
      ...currentBookedItems.filter(
        item =>
          item.item.relatedBookedItemId === undefined && item.item.itemType !== ApiItemType.PACKAGE
      )
    ];
  }

  return currentBookedItems.reduce((total: { item: ApiBookedItem; amount: number }[], current) => {
    if (
      isOptionalFlight(current.item) &&
      total.some(totalItem => isOptionalFlight(totalItem.item)) &&
      !!travelerId
    ) {
      return total.map(totalItem => {
        if (isOptionalFlight(totalItem.item)) {
          return {
            ...totalItem,
            item: {
              ...totalItem.item,
              priceByPersonId: {
                ...totalItem.item.priceByPersonId,
                [travelerId]: {
                  ...totalItem.item.priceByPersonId[travelerId],
                  finalPrice:
                    (totalItem.item?.priceByPersonId?.[travelerId]?.finalPrice || 0) +
                    (current.item?.priceByPersonId?.[travelerId]?.finalPrice || 0)
                }
              }
            }
          };
        } else {
          return totalItem;
        }
      });
    } else {
      return [...total, current];
    }
  }, []);
};

const Summary = (props: SummaryProperties): JSX.Element => {
  const { t } = useTranslation('summary', fallback, Keys);
  const [priceDetailsModal, setPriceDetailsModal] = useState(false);

  const {
    booking,
    view = ItemComponentView.SUMMARY,
    showTotal,
    showDivider,
    onDelete,
    onEdit,
    bookedItemsOpen,
    showInfo = true,
    showTraveler = true,
    showTravelerSeparate = false,
    travelers: externalTravelers,
    showSsr = true,
    showAdditionalInfo = false,
    withPriceDetailsModal = false,
    showSeats,
    bookedItems,
    withIconColumn,
    hideSinglePrices,
    customFlightDateDisplay,
    customPriceModifier,
    showNumberOfPerson,
    order,
    header,
    showDiscounts,
    showFees,
    groupSameItems,
    bookedItemsContainerClass,
    relatedItems,
    showPackageRelatedItems,
    displayExtraServiceDate,
    displayExtraServiceTime,
    displayRelatedItems,
    showPaxInSummary,
    useCustomItemOrder,
    renderExternalContent,
    showSsrPriceSubtotal,
    showPricePlusMinus,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    customItemRegistry,
    showOnlyPriceOfNewItems,
    customLabelsMapper,
    isCollapsible = false,
    onCollapseToggle,
    activeCollapsibleIndexes = [],
    isInEditMode,
    customPerTravelerSummaryItem
  } = props;
  const [openedCollapsibles, setOpenedCollapsibles] = useState<number[]>(activeCollapsibleIndexes);

  if (!booking || !booking.bookedItems || booking.bookedItems.length === 0) {
    return <div>{t(Keys.emptyCart)}</div>;
  }

  const checkShowPlusMinus = (item: ApiBookedItem): boolean => {
    if (showPricePlusMinus) {
      return showPricePlusMinus(item);
    }
    return true;
  };

  let priceTax = PriceFactory.create(0, booking.price.currencyCode);
  const priceDiscounts = PriceFactory.create(0, booking.price.currencyCode);
  let feeValue = booking.openAmount.modifiers.find(m => m.type === ApiPriceModifierType.FEE);

  PriceUtil.appendTaxes(booking.price, priceTax);

  PriceUtil.appendDiscounts(booking.price, priceDiscounts);

  let priceSubtotal = PriceFactory.create(
    booking.price.finalPrice - priceTax.finalPrice - (showFees && feeValue ? feeValue.absolute : 0),
    booking.price.currencyCode
  );

  let priceTotal = PriceFactory.create(
    booking.price.finalPrice + priceDiscounts.finalPrice,
    booking.price.currencyCode
  );

  if (showOnlyPriceOfNewItems) {
    const newItemsPrice = PriceUtil.getPriceOfOnlyNewItems(booking);
    priceTotal = booking?.changedAmount || newItemsPrice;
    feeValue = priceTotal.modifiers.find(m => m.type === ApiPriceModifierType.FEE);
    priceTax = PriceFactory.create(0, booking.price.currencyCode);

    PriceUtil.appendTaxes(booking.price, priceTax);
    PriceUtil.appendDiscounts(booking.price, priceDiscounts);

    priceSubtotal = PriceFactory.create(
      newItemsPrice.finalPrice - (showFees && feeValue ? feeValue.absolute : 0),
      booking.price.currencyCode
    );
  }

  const showSeatsPrice =
    showSeats &&
    !!booking.bookedItems.filter(bookedItem => bookedItem.itemType === ApiItemType.SEAT).length;
  const seatSubtotal = PriceFactory.create(
    booking.bookedItems
      .filter(bookedItem => bookedItem.itemType === ApiItemType.SEAT)
      .map(seat => seat.price.finalPrice)
      .reduce((prev, curr) => prev + curr, 0),
    booking.price.currencyCode
  );

  const showTotalPricePerPerson = booking.travelers.map(tr => {
    return PriceFactory.create(
      bookedItems &&
        bookedItems
          .filter(
            bookedItem =>
              bookedItem.itemType !== ApiItemType.PACKAGE && bookedItem.priceByPersonId[tr.id]
          )
          .map(it => {
            return it.priceByPersonId[tr.id].finalPrice;
          })
          .reduce((prev, curr) => prev + curr, 0),
      booking.price.currencyCode
    );
  });

  const showTotalDiscountPerPerson = booking.travelers.map(tr => {
    return PriceFactory.create(
      bookedItems &&
        booking.bookedItems
          .filter(it => it.type !== ApiItemType.PACKAGE)
          .map(bt =>
            bt.priceByPersonId[tr.id]?.modifiers.filter(
              mod => mod?.type === ApiPriceModifierType.DISCOUNT
            )
          )
          .filter((md, idx) => !!md && md[idx])
          .reduce((prev, curr, idx) => {
            return prev + curr[idx]?.absolute;
          }, 0),
      booking.price.currencyCode
    );
  });

  const relatedItemsValue = (item: ApiBookedItem): ApiBookedItem[] => {
    let foundItem: Array<ApiBookedItem> = [];
    relatedItems?.forEach((value, key) => {
      if (key.id === item.id) {
        foundItem = value;
      } else if (value.some(v => v.id === item.id)) {
        foundItem = value;
      }
    });

    return foundItem;
  };

  const hasRelatedItems = (item: ApiBookedItem): boolean => {
    return !!relatedItemsValue(item).length;
  };

  const ssrsPriceSubtotal = booking.bookedItems
    .map(v => {
      if (!v.specialRequests) {
        return PriceFactory.create(0, booking.price.currencyCode);
      }
      return v.specialRequests
        .map(s => s.fee)
        .reduce((a, c) => {
          if (!a) {
            return c;
          }
          return PriceFactory.create(a.finalPrice + (c?.finalPrice || 0), a.currencyCode);
        }, PriceFactory.create(0, booking.price.currencyCode));
    })
    .reduce((a, c) => {
      if (!a) {
        return c;
      }
      return PriceFactory.create(a.finalPrice + (c?.finalPrice || 0), a.currencyCode);
    }, PriceFactory.create(0, booking.price.currencyCode));

  const priceDetails = () => (
    <Row className="text-right mx-3">
      {booking.price.modifiers.map(modifier => (
        <>
          <Col xs={8} className="mt-md-3">
            {modifier.name}
          </Col>
          <Col xs={4} className="mt-md-3">
            <b>
              <Price
                price={PriceFactory.create(
                  modifier.absolute,
                  modifier.currencyCode || booking?.price.currencyCode
                )}
              />
            </b>
          </Col>
        </>
      ))}
      <Col xs={12}>
        <hr />
      </Col>
      <Col xs={8}>{t(Keys.subTotal)}</Col>
      <Col xs={4}>
        <b>
          <Price price={priceSubtotal} />
        </b>
      </Col>
      <Col xs={8} className="mt-md-3">
        {t(Keys.taxes)}
      </Col>
      <Col xs={4} className="mt-md-3">
        <b>
          <Price price={priceTax} />
        </b>
      </Col>
      <Col xs={8} className="price h5 mt-3 mt-md-5">
        {t(Keys.total)}
      </Col>
      <Col xs={4} className="price h5 mt-3 mt-md-5">
        <b>
          <Price price={booking.price} className="summary__price" />
        </b>
      </Col>
      <Col xs={12} className="mt-3 mt-md-5">
        <Button
          color="secondary"
          className="w-50"
          outline
          onClick={() => setPriceDetailsModal(false)}
        >
          {t(Keys.close)}
        </Button>
      </Col>
    </Row>
  );

  const toggleCollapsible = (travelerIdx: number) => {
    setOpenedCollapsibles(prev => {
      if (prev.includes(travelerIdx)) return prev.filter(val => val !== travelerIdx);
      return prev.concat([travelerIdx]);
    });
    if (onCollapseToggle) onCollapseToggle(travelerIdx);
  };

  const firstRideItem = getBookedItemsList(
    bookedItems,
    booking.bookedItems,
    undefined,
    showPackageRelatedItems
  ).find(
    it =>
      (it.item.itemType === ApiItemType.FLIGHT &&
        it.item.componentType !== ApiComponentType.OPTIONAL) ||
      (it.item.itemType === ApiItemType.BUS && it.item.componentType !== ApiComponentType.OPTIONAL)
  );
  return (
    <>
      <div className={bookedItemsContainerClass || ''}>
        <div>
          {showTravelerSeparate && firstRideItem ? (
            <>
              <div key={firstRideItem.item.id}>
                {header && <div className="iso__summary__header">{header}</div>}
                <div className="justify-content-between mb-1">
                  <div className="flex-grow-1">
                    <BookedItemComponent
                      registry={
                        customItemRegistry ? mergeItemRegisters(Items, customItemRegistry) : Items
                      }
                      booking={booking}
                      item={firstRideItem.item}
                      view={view}
                      showInfo={showInfo}
                      showTraveler={showTraveler}
                      showTravelerSeparate={showTravelerSeparate}
                      showSsr={showSsr}
                      isOpen={bookedItemsOpen}
                      onDelete={onDelete}
                      onEdit={onEdit}
                      showAdditionalInfo={showAdditionalInfo}
                      withIconColumn={true}
                      hideSinglePrices={hideSinglePrices}
                      customFlightDateDisplay={customFlightDateDisplay}
                      customPriceModifier={customPriceModifier}
                      showNumberOfPerson={showNumberOfPerson}
                      order={order}
                      displayExtraServiceDate={displayExtraServiceDate}
                      displayExtraServiceTime={displayExtraServiceTime}
                      displayRelatedItems={displayRelatedItems}
                      showPax={showPaxInSummary}
                      useCustomItemOrder={useCustomItemOrder}
                      renderExternalContent={renderExternalContent}
                      customLabelsMapper={customLabelsMapper}
                      isInEditMode={isInEditMode}
                    />
                  </div>
                </div>
              </div>

              {booking.travelers.map((traveler, travelerIdx) => {
                const bookedItemList = getBookedItemsList(
                  bookedItems,
                  booking.bookedItems,
                  false,
                  showPackageRelatedItems,
                  undefined,
                  traveler.id
                ).filter(
                  it =>
                    it.item.priceByPersonId[traveler.id] &&
                    (it.item.itemType !== ApiItemType.FLIGHT ||
                      (it.item.itemType === ApiItemType.FLIGHT &&
                        (it.item.componentType !== ApiComponentType.OPTIONAL ||
                          (it.item.componentType === ApiComponentType.OPTIONAL &&
                            it.item.priceByPersonId[traveler.id].finalPrice !== undefined))))
                );
                const currentTraveler =
                  !!externalTravelers && !!externalTravelers[travelerIdx]
                    ? externalTravelers[travelerIdx]
                    : traveler;
                const isOpen = openedCollapsibles.includes(travelerIdx);

                if (customPerTravelerSummaryItem) {
                  const CustomPerTravelerSummaryItem = customPerTravelerSummaryItem(
                    traveler,
                    bookedItemList,
                    travelerIdx
                  );
                  return CustomPerTravelerSummaryItem;
                }

                return (
                  <Fragment key={traveler.id}>
                    {showDivider && <hr />}

                    <SummaryItemHeader
                      isOpen={isOpen}
                      toggleCollapsible={() => toggleCollapsible(travelerIdx)}
                      isCollapsible={isCollapsible}
                    >
                      <div>
                        {isCollapsible && (
                          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                        )}
                        <div className="summary__traveler mt-3">
                          {!!currentTraveler.firstName || !!currentTraveler.lastName
                            ? `${currentTraveler.firstName || ''}${
                                !!currentTraveler.firstName && !!currentTraveler.lastName ? ' ' : ''
                              }${currentTraveler.lastName || ''}`
                            : `${travelerIdx + 1}. ${t(Keys.participant)}`}
                        </div>
                      </div>
                      {isCollapsible && (
                        <Price
                          price={showTotalPricePerPerson[travelerIdx]}
                          className="summary__price-per-person"
                        />
                      )}
                    </SummaryItemHeader>
                    <SummaryItemContent isOpen={isOpen} isCollapsible={isCollapsible}>
                      {bookedItemList.map((bookedItem, idx) => {
                        let item = { ...bookedItem };
                        // price in required flight contains airport surcharge; has to be rendered in travelers list
                        if (isRequiredFlight(item.item) || isOptionalFlight(item.item)) {
                          item = {
                            ...item,
                            item: {
                              ...item.item,
                              name: `${
                                isRequiredFlight(item.item)
                                  ? t(Keys.airportSurcharge)
                                  : item.item.name
                              }${item.amount > 1 ? ` - ${item.amount} x` : ''}`,
                              useValuePriceDisplay: true
                            }
                          };
                        }

                        return (
                          <div key={item.item.id}>
                            {header && idx === 0 && (
                              <div className="iso__summary__header">{header}</div>
                            )}
                            <div className="justify-content-between mb-1">
                              <div className="flex-grow-1">
                                <BookedItemComponent
                                  registry={
                                    customItemRegistry
                                      ? mergeItemRegisters(Items, customItemRegistry)
                                      : Items
                                  }
                                  booking={booking}
                                  item={{ ...item.item }}
                                  travelerId={traveler.id}
                                  view={view}
                                  showInfo={showInfo}
                                  showTraveler={showTraveler}
                                  showTravelerSeparate={showTravelerSeparate}
                                  showSsr={showSsr}
                                  isOpen={bookedItemsOpen}
                                  onDelete={onDelete}
                                  onEdit={onEdit}
                                  showAdditionalInfo={showAdditionalInfo}
                                  withIconColumn={withIconColumn}
                                  hideSinglePrices={hideSinglePrices}
                                  customFlightDateDisplay={customFlightDateDisplay}
                                  customPriceModifier={customPriceModifier}
                                  showNumberOfPerson={showNumberOfPerson}
                                  order={order}
                                  relatedItems={relatedItemsValue(item.item)}
                                  displayExtraServiceDate={displayExtraServiceDate}
                                  displayExtraServiceTime={displayExtraServiceTime}
                                  displayRelatedItems={displayRelatedItems}
                                  showPax={showPaxInSummary}
                                  useCustomItemOrder={useCustomItemOrder}
                                  renderExternalContent={renderExternalContent}
                                  useNameForDescription
                                  showPricePlusMinus={checkShowPlusMinus(item.item)}
                                  customLabelsMapper={customLabelsMapper}
                                  isInEditMode={isInEditMode}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {showDiscounts && !!showTotalDiscountPerPerson[travelerIdx].finalPrice && (
                        <div>
                          <div className="justify-content-between mb-1">
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between">
                                <div>
                                  <h6 className="my-0  iso__summary__discount-per-person__description">
                                    {t(Keys.voucher)}
                                  </h6>
                                </div>
                                <div className="d-flex align-items-center justify-content-end ">
                                  <span className=" iso__summary__discount-per-person__price ml-1">
                                    <Price price={showTotalDiscountPerPerson[travelerIdx]} />
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {!isCollapsible && (
                        <Price
                          prefix={`${t(Keys.totalPerPerson)} `}
                          price={showTotalPricePerPerson[travelerIdx]}
                          className="summary__price-per-person"
                        />
                      )}
                    </SummaryItemContent>
                  </Fragment>
                );
              })}
            </>
          ) : (
            <>
              {getBookedItemsList(
                bookedItems,
                booking.bookedItems,
                groupSameItems,
                showPackageRelatedItems,
                hasRelatedItems
              ).map((item, idx) => (
                <div key={item.item.id}>
                  {header && idx === 0 && <div className="iso__summary__header">{header}</div>}
                  <div className="justify-content-between mb-1">
                    <div className="flex-grow-1">
                      <BookedItemComponent
                        registry={
                          customItemRegistry ? mergeItemRegisters(Items, customItemRegistry) : Items
                        }
                        booking={booking}
                        item={{
                          ...item.item,
                          name: `${item.item.name}${item.amount > 1 ? ` - ${item.amount} x` : ''}`
                        }}
                        view={view}
                        showInfo={showInfo}
                        showTraveler={showTraveler}
                        showSsr={showSsr}
                        isOpen={bookedItemsOpen}
                        onDelete={onDelete}
                        onEdit={onEdit}
                        showAdditionalInfo={showAdditionalInfo}
                        withIconColumn={withIconColumn}
                        hideSinglePrices={hideSinglePrices}
                        customFlightDateDisplay={customFlightDateDisplay}
                        customPriceModifier={customPriceModifier}
                        showNumberOfPerson={showNumberOfPerson}
                        order={order}
                        relatedItems={relatedItemsValue(item.item)}
                        displayExtraServiceDate={displayExtraServiceDate}
                        displayExtraServiceTime={displayExtraServiceTime}
                        displayRelatedItems={displayRelatedItems}
                        showPax={showPaxInSummary}
                        useCustomItemOrder={useCustomItemOrder}
                        renderExternalContent={renderExternalContent}
                        customLabelsMapper={customLabelsMapper}
                        isInEditMode={isInEditMode}
                      />
                    </div>
                  </div>
                  {showDivider && <hr />}
                </div>
              ))}
            </>
          )}
        </div>
        {showDiscounts &&
          booking.price.modifiers
            .filter(m => m.type === ApiPriceModifierType.DISCOUNT)
            .map(discount => (
              <div>
                <div className="justify-content-between mb-1">
                  <div className="flex-grow-1">
                    <Row>
                      <Col>
                        <Row className="align-items-center">
                          <Col>
                            <h6 className="my-0">{t(Keys.voucher)}</h6>
                          </Col>
                        </Row>
                        <Row className="p-0">
                          <Col className="d-flex align-items-center justify-content-end ">
                            <span className="text-muted ml-1">
                              <Price
                                price={PriceFactory.create(
                                  discount.absolute,
                                  booking?.price.currencyCode
                                )}
                              />
                            </span>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </div>
                </div>
                {showDivider && <hr />}
              </div>
            ))}
      </div>
      {showTotal && booking.price && (
        <>
          {(priceTax.finalPrice > 0 ||
            (feeValue && showFees) ||
            (showDiscounts && priceDiscounts.finalPrice !== 0)) && (
            <div className="summary__taxes text-right mt-3">
              {showSeatsPrice && (
                <TaxSpan>
                  <Price
                    prefix={`${t(Keys.seats)}: `}
                    price={seatSubtotal}
                    className="summary__price"
                  />
                </TaxSpan>
              )}
              {showSsrPriceSubtotal && (
                <TaxSpan>
                  <Price
                    prefix={`${t(Keys.ssrPriceSubtotal)}: `}
                    price={ssrsPriceSubtotal}
                    className="summary__price"
                  />
                </TaxSpan>
              )}
              <TaxSpan>
                <Price
                  prefix={`${t(Keys.subTotal)}: `}
                  price={priceSubtotal}
                  className="summary__price"
                />
              </TaxSpan>
              {priceTax.finalPrice > 0 && (
                <TaxSpan>
                  <Price
                    prefix={`${t(Keys.taxes)}: `}
                    price={priceTax}
                    className="summary__price"
                  />
                </TaxSpan>
              )}
              {showDiscounts && (
                <TaxSpan>
                  <Price
                    prefix={`${t(Keys.discounts)}: `}
                    price={priceDiscounts}
                    className="summary__price"
                  />
                </TaxSpan>
              )}
              {showFees && feeValue && (
                <TaxSpan>
                  <Price
                    prefix={`${t(Keys.fee)}: `}
                    price={PriceFactory.create(feeValue.absolute, booking?.price.currencyCode)}
                    className="summary__price"
                  />
                </TaxSpan>
              )}
            </div>
          )}
          <div className="summary__total-price price text-right h5 mt-3">
            {t(Keys.total)}:{' '}
            <b>
              <Price price={priceTotal} className="summary__price" />
            </b>
          </div>
        </>
      )}
      {withPriceDetailsModal && booking.price && (
        <>
          <div className="d-flex justify-content-center justify-content-md-end h5 mt-3">
            <Button
              color="link"
              className="total-price-details-label pr-md-0 text-primary font-weight-bold"
              onClick={(): void => setPriceDetailsModal(true)}
            >
              {t(Keys.priceDetails)}
            </Button>
          </div>
          <BasicModal
            isOpen={priceDetailsModal}
            itemToDisplay={priceDetails()}
            toggle={() => setPriceDetailsModal(prevState => !prevState)}
            className="summary__price-detail_modal"
          />
        </>
      )}
    </>
  );
};

interface CollapsibleSummaryProps {
  isOpen?: boolean;
  children: ReactNode;
  isCollapsible?: boolean;
}

const SummaryItemHeader = ({
  children,
  isCollapsible,
  toggleCollapsible,
  isOpen
}: {
  children: ReactNode;
  isCollapsible?: boolean;
  toggleCollapsible: () => void;
  isOpen?: boolean;
}) => {
  if (isCollapsible) {
    return (
      <div
        onClick={toggleCollapsible}
        className={`mt-3 summary__collapsible__header ${
          isOpen ? 'summary__collapsible__header--open' : ''
        }`}
      >
        {children}
      </div>
    );
  }
  return <div className="mt-3">{children}</div>;
};

const SummaryItemContent = ({ children, isOpen, isCollapsible }: CollapsibleSummaryProps) => {
  if (!isCollapsible) return <>{children}</>;
  return <Collapse isOpen={isOpen}>{children}</Collapse>;
};

export default Summary;
